import { createContext, memo, useCallback, type PropsWithChildren } from 'react';
import { map, shareReplay } from 'rxjs/operators';
import { v1 as uuid } from 'uuid';

import {
  CUSTOMER,
  CUSTOMER_ADDRESS,
  CUSTOMER_ROLE,
  CUSTOMER_USER,
  CustomerBalanceTransactionTypeEnum,
  DEFAULT_BIGGER_PAGINATION_SIZE,
  DELETE,
  GET,
  ModeEnum,
  PATCH,
  POST,
  PUT,
  TreasuryLinkDirectionEnum,
  TreasuryLinkTypeEnum,
  formattedDateForSubscription,
  request,
  toCreateCustomerUserApiKeyRequestBody,
  useObservable,
  useStaticSubscription,
  useUserContext,
  wsScanToMap,
  type Customer,
  type CustomerRole,
  type CustomerTradingLimit,
  type CustomerTransaction,
  type CustomerUser,
  type CustomerUserApiKey,
  type CustomerUserApiKeyPermissionsUpdate,
  type ICustomerAddress,
  type ICustomerFIXConnection,
  type ICustomerOrderExecutionRule,
} from '@talos/kyoko';
import { DatadogTradingAction, trackCustomerOrderAction } from '../utils/datadogTradingActions';
import type {
  CreateCustomerRequest,
  CustomerAddressResponse,
  CustomerTradeUpdate,
  CustomerTransferProps,
  CustomerUserConfig,
  CustomersContextProps,
  MarketAccountAuthorization,
} from './CustomersContext.types';

export const CustomersContext = createContext<CustomersContextProps | null>(null);
CustomersContext.displayName = 'CustomersContext';

export const CustomersProvider = memo(function CustomersProvider(props: PropsWithChildren<unknown>) {
  const { data: customersSubscription, isLoading } = useStaticSubscription<Customer>(
    { name: CUSTOMER, IncludePricingRuleMode: true, tag: 'CustomersProvider', limit: DEFAULT_BIGGER_PAGINATION_SIZE },
    { loadAll: true }
  );

  const customers = useObservable(
    () =>
      customersSubscription.pipe(
        wsScanToMap({ getUniqueKey: d => d.CounterpartyID, newMapEachUpdate: false }),
        map(map => [...map.values()]),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [customersSubscription]
  );

  const { data: customerUsersSub } = useStaticSubscription<CustomerUser>(
    { name: CUSTOMER_USER, tag: 'CustomersProvider' },
    { loadAll: true }
  );

  const customerUsers = useObservable(
    () =>
      customerUsersSub.pipe(
        wsScanToMap({ getUniqueKey: d => d.CustomerUserID, newMapEachUpdate: false }),
        map(map => [...map.values()]),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [customerUsersSub]
  );

  const { data: customerRolesSub } = useStaticSubscription<CustomerRole>({
    name: CUSTOMER_ROLE,
    tag: 'CustomersProvider',
  });

  const customerRoles = useObservable(
    () =>
      customerRolesSub.pipe(
        wsScanToMap({ getUniqueKey: d => d.RoleID, newMapEachUpdate: false }),
        map(map => [...map.values()]),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [customerRolesSub]
  );

  const { orgApiEndpoint } = useUserContext();
  const customerEndpoint = `${orgApiEndpoint}/customer`;
  const customersEndpoint = `${orgApiEndpoint}/customers`;
  const customerUserEndpoint = `${orgApiEndpoint}/customer-users`;
  const customerConfigurationsEndpoint = `${orgApiEndpoint}/customer-configurations`;
  const customerBalancesEndpoint = `${orgApiEndpoint}/customer-balances`;
  const customerAddressesEndpoint = `${orgApiEndpoint}/customer-addresses`;
  const customerTreasuryEndpoint = `${orgApiEndpoint}/treasury`;
  const organizationEndpoint = `${orgApiEndpoint}/organization`;

  const getCustomerUserConfigsEndpoint = useCallback(
    (customerUserID: string) => `${customerUserEndpoint}/${customerUserID}/configs`,
    [customerUserEndpoint]
  );

  const getEffectiveLinkForCustomerTransaction = useCallback(
    (entity: Pick<CustomerTransaction, 'Currency' | 'MarketAccount' | 'TransactionType'>) => {
      if (!entity.Currency || !entity.MarketAccount) {
        return Promise.resolve({ data: [] });
      }
      const params = new URLSearchParams({
        Currency: entity.Currency,
        Direction:
          entity.TransactionType === CustomerBalanceTransactionTypeEnum.Deposit
            ? TreasuryLinkDirectionEnum.Inbound
            : TreasuryLinkDirectionEnum.Outbound,
        MarketAccount: entity.MarketAccount,
        Type: TreasuryLinkTypeEnum.CustomerTransfer,
      });
      return request(GET, `${customerTreasuryEndpoint}/accounts/effective-link?${params.toString()}`);
    },
    [customerTreasuryEndpoint]
  );

  const createCustomer = useCallback(
    (customer: CreateCustomerRequest) => request(POST, customersEndpoint, customer),
    [customersEndpoint]
  );
  const updateCustomer = useCallback(
    (customer: Partial<Customer>) => request(PATCH, customersEndpoint, customer),
    [customersEndpoint]
  );
  const deleteCustomer = useCallback(
    (customerName: string) => request(DELETE, `${customersEndpoint}/${encodeURIComponent(customerName)}`),
    [customersEndpoint]
  );
  const createCustomerUser = useCallback(
    (customerUser: Partial<CustomerUser>) => request(POST, `${customerUserEndpoint}`, customerUser),
    [customerUserEndpoint]
  );
  const updateCustomerUser = useCallback(
    (customerUser: Partial<CustomerUser>) =>
      request(PATCH, `${customerUserEndpoint}/${customerUser.CustomerUserID}`, customerUser),
    [customerUserEndpoint]
  );
  const updateCustomerUserRole = useCallback(
    (customerUserID: string, initialRoles?: string[], newRoles?: string[]) => {
      const rolesToDisable = initialRoles?.filter(role => !newRoles?.includes(role)) ?? [];
      const rolesToEnable = newRoles?.filter(role => !initialRoles?.includes(role)) ?? [];
      return request(
        POST,
        `${customerUserEndpoint}/roles`,
        rolesToDisable
          .map(role => getRole(role, ModeEnum.Disabled))
          .concat(rolesToEnable.map(role => getRole(role, ModeEnum.Enabled)))
      );

      function getRole(role: string, mode: ModeEnum) {
        return {
          CustomerUserID: customerUserID,
          RoleID: role,
          Mode: mode,
        };
      }
    },
    [customerUserEndpoint]
  );
  const getOrderExecutionRulesGlobalStatus = useCallback(
    () =>
      request(GET, `${customerEndpoint}/order-execution-rules/global-status`, null).then(
        (response: { ExecutionRulesGlobalStatus: ModeEnum }) => {
          return response.ExecutionRulesGlobalStatus === ModeEnum.Enabled;
        }
      ),
    [customerEndpoint]
  );
  const updateOrderExecutionRulesGlobalStatus = useCallback(
    (status: 'enable' | 'disable') =>
      request(PATCH, `${customerEndpoint}/order-execution-rules/global-status/${status}`, null).then(
        (response: { ExecutionRulesGlobalStatus: ModeEnum }) => {
          return response.ExecutionRulesGlobalStatus === ModeEnum.Enabled;
        }
      ),
    [customerEndpoint]
  );
  const getCustomerRoles = useCallback(() => request(GET, `${customerUserEndpoint}/roles`), [customerUserEndpoint]);
  const getCustomerConfigs = useCallback(
    () => request(GET, customerConfigurationsEndpoint, null, { paginateRecords: Infinity }),
    [customerConfigurationsEndpoint]
  );
  const getCustomerUserConfigs = useCallback(
    (customerUserID: string): Promise<CustomerUserConfig[]> => {
      return request(GET, `${getCustomerUserConfigsEndpoint(customerUserID)}`).then(configs => configs.data);
    },
    [getCustomerUserConfigsEndpoint]
  );
  const upsertCustomerUserConfigs = useCallback(
    (configs: Array<CustomerUserConfig & { CustomerUserID: string }>): Promise<CustomerUserConfig[]> => {
      return request(POST, `${customerUserEndpoint}/configs`, configs).then(configs => configs.data);
    },
    [customerUserEndpoint]
  );
  const upsertCustomerUserConfig = useCallback(
    <T,>(customerUserID: string, configKey: string, value: T): Promise<CustomerUserConfig[]> => {
      return upsertCustomerUserConfigs([
        {
          CustomerUserID: customerUserID,
          Key: configKey,
          Value: JSON.stringify(value),
        },
      ]);
    },
    [upsertCustomerUserConfigs]
  );
  const removeCustomerUserConfig = useCallback(
    (customerUserID: string, configKey: string): Promise<null> => {
      return request(DELETE, `${customerUserEndpoint}/${customerUserID}/configs/${configKey}`, null);
    },
    [customerUserEndpoint]
  );
  const getCustomerUserAPIKeys = useCallback(
    (userID: string) => request(GET, `${customerUserEndpoint}/${userID}/api-key`, null),
    [customerUserEndpoint]
  );
  const createCustomerUserAPIKey = useCallback(
    (userID: string, label?: string) =>
      request(POST, `${customerUserEndpoint}/${userID}/api-key`, toCreateCustomerUserApiKeyRequestBody(label)),
    [customerUserEndpoint]
  );
  const deleteCustomerUserAPIKey = useCallback(
    (userID: string, apiKeyID: string) =>
      request(DELETE, `${customerUserEndpoint}/${userID}/api-key/${apiKeyID}`, null),
    [customerUserEndpoint]
  );
  const updateCustomerUserAPIKey = useCallback(
    (customerUserApiKey: CustomerUserApiKey) =>
      request(PATCH, `${customerUserEndpoint}/${customerUserApiKey.CustomerUserID}/api-key`, customerUserApiKey),
    [customerUserEndpoint]
  );
  const updateCustomerUserAPIKeyPermissions = useCallback(
    (userID: string, apiKeyID: string, permissionsUpdate: CustomerUserApiKeyPermissionsUpdate) =>
      request(PATCH, `${customerUserEndpoint}/${userID}/api-key/${apiKeyID}/permissions`, permissionsUpdate),
    [customerUserEndpoint]
  );
  const customerTradingLimitsEndpoint = `${customerEndpoint}/max-order-size-limits`;
  const createCustomerTradingLimit = useCallback(
    (customerUserTradingLimit: CustomerTradingLimit) =>
      request(POST, `${customerTradingLimitsEndpoint}`, customerUserTradingLimit),
    [customerTradingLimitsEndpoint]
  );
  const updateCustomerTradingLimit = useCallback(
    (customerUserTradingLimit: CustomerTradingLimit) =>
      request(
        PUT,
        `${customerTradingLimitsEndpoint}/${customerUserTradingLimit.TradingLimitID}`,
        customerUserTradingLimit
      ),
    [customerTradingLimitsEndpoint]
  );
  const deleteCustomerTradingLimit = useCallback(
    (tradingLimitID: string) => request(DELETE, `${customerTradingLimitsEndpoint}/${tradingLimitID}`, null),
    [customerTradingLimitsEndpoint]
  );

  const customerExecutionRulesEndpoint = `${customerEndpoint}/order-execution-rules`;
  const getCustomerExecutionRules = useCallback(
    () => request(GET, customerExecutionRulesEndpoint, null),
    [customerExecutionRulesEndpoint]
  );
  const createCustomerExecutionRule = useCallback(
    (customerUserExecutionRule: ICustomerOrderExecutionRule) =>
      request(POST, customerExecutionRulesEndpoint, customerUserExecutionRule),
    [customerExecutionRulesEndpoint]
  );
  const editCustomerExecutionRule = useCallback(
    (customerUserExecutionRule: ICustomerOrderExecutionRule) =>
      request(
        PATCH,
        `${customerExecutionRulesEndpoint}/${customerUserExecutionRule.RuleID}`,
        customerUserExecutionRule
      ),
    [customerExecutionRulesEndpoint]
  );
  const deleteCustomerExecutionRule = useCallback(
    (ruleID: string) => request(DELETE, `${customerExecutionRulesEndpoint}/${ruleID}`, null),
    [customerExecutionRulesEndpoint]
  );

  const customerFIXConnectionsEndpoint = `${organizationEndpoint}/customer-fix-connections`;
  const getCustomerFIXConnections = useCallback(
    () => request(GET, customerFIXConnectionsEndpoint, null),
    [customerFIXConnectionsEndpoint]
  );

  const upsertCustomerFIXConnection = useCallback(
    (customerFIXConnection: Partial<ICustomerFIXConnection>[]) =>
      request(PUT, customerFIXConnectionsEndpoint, customerFIXConnection),
    [customerFIXConnectionsEndpoint]
  );

  const deleteCustomerFIXConnection = useCallback(
    (targetCompID: string) => request(DELETE, `${customerFIXConnectionsEndpoint}/${targetCompID}`, null),
    [customerFIXConnectionsEndpoint]
  );

  const customerTradeEndpoint = `${customerEndpoint}/trade`;
  const createCustomerTrade = useCallback(
    (customerTrade: CustomerTradeUpdate) => request(POST, `${customerTradeEndpoint}`, customerTrade),
    [customerTradeEndpoint]
  );

  const customerTradesEndpoint = `${customerEndpoint}/trades`;
  const updateCustomerTrade = useCallback(
    (customerTrade: CustomerTradeUpdate) =>
      request(PATCH, `${customerTradesEndpoint}/${customerTrade.TradeID}`, customerTrade),
    [customerTradesEndpoint]
  );

  const customerOrdersEndpoint = `${customerEndpoint}/orders`;
  const modifyCustomerOrderSpread = useCallback(
    (orderID: string, spread: string) => {
      trackCustomerOrderAction(DatadogTradingAction.ModifySpread, { orderID });
      return request(POST, `${customerOrdersEndpoint}/${orderID}/update-parameters`, {
        Spread: spread,
      });
    },
    [customerOrdersEndpoint]
  );

  const { data: customerAddressSub } = useStaticSubscription<CustomerAddressResponse>({
    name: CUSTOMER_ADDRESS,
    tag: 'CustomersProvider',
  });

  const customerAddressesByCustomerAddressID = useObservable(
    () =>
      customerAddressSub.pipe(
        wsScanToMap({ getUniqueKey: d => d.CustomerAddressID, newMapEachUpdate: true }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [customerAddressSub]
  );

  const approvePendingApprovalTransaction = useCallback(
    (customerTransaction: CustomerTransaction) =>
      request(PUT, `${customerBalancesEndpoint}/transactions/${customerTransaction.TransactionID}/accept`, {
        TransferID: customerTransaction.TransferID,
      }),
    [customerBalancesEndpoint]
  );
  const approvePendingTransferTransaction = useCallback(
    (customerTransaction: CustomerTransaction, txhash?: string) =>
      request(PUT, `${customerTreasuryEndpoint}/transactions/${customerTransaction.TransactionID}/accept`, {
        TransferID: customerTransaction.TransferID,
        TxHash: txhash,
      }),
    [customerTreasuryEndpoint]
  );
  const initiatePendingTransferTransaction = useCallback(
    ({
      amount,
      clientID = uuid(),
      currency,
      transferCurrency,
      description,
      fromMarketAccountID,
      market,
      reference,
      toMarketAccountID,
      transactionID,
      chainCurrency,
      transactTime = new Date(),
    }: CustomerTransferProps) =>
      request(POST, `${customerTreasuryEndpoint}/transactions/${transactionID}/transfer`, {
        ClientID: clientID,
        Currency: currency,
        TransferCurrency: transferCurrency,
        Amount: amount,
        Market: market,
        TransactTime: formattedDateForSubscription(transactTime),
        FromMarketAccountID: fromMarketAccountID,
        ToMarketAccountID: toMarketAccountID,
        Description: description,
        ReferenceData: reference,
        ChainCurrencySymbol: chainCurrency,
      }),
    [customerTreasuryEndpoint]
  );
  const rejectPendingApprovalTransaction = useCallback(
    (customerTransaction: CustomerTransaction) =>
      request(PUT, `${customerBalancesEndpoint}/transactions/${customerTransaction.TransactionID}/reject`, {
        TransferID: customerTransaction.TransferID,
      }),
    [customerBalancesEndpoint]
  );

  const getCustomerWithdrawAddress = useCallback(
    (customerAddressID: string) => request(GET, `${customerAddressesEndpoint}/${customerAddressID}`),
    [customerAddressesEndpoint]
  );

  const createCustomerAddress = useCallback(
    (addressDetails: Partial<ICustomerAddress>) => request(POST, customerAddressesEndpoint, addressDetails),
    [customerAddressesEndpoint]
  );

  const confirmCustomerAddress = useCallback(
    (customerAddressID: string) => request(PUT, `${customerAddressesEndpoint}/${customerAddressID}/confirm`, null),
    [customerAddressesEndpoint]
  );

  const rejectPendingTransferTransaction = useCallback(
    (customerTransaction: CustomerTransaction) =>
      request(PUT, `${customerTreasuryEndpoint}/transactions/${customerTransaction.TransactionID}/reject`, {
        TransferID: customerTransaction.TransferID,
      }),
    [customerTreasuryEndpoint]
  );

  const addOrDeleteMarketAccountAuthorization = useCallback(
    (customerUserID: string, authorizations: MarketAccountAuthorization[], type: 'add' | 'delete') => {
      return request(
        PATCH,
        `${customerUserEndpoint}/${customerUserID}/${type}-authorizations`,
        authorizations.map(authorization => ({
          CustomerUserID: customerUserID,
          Action: authorization.authorization,
          Filter: { Type: 'MarketAccount', Value: authorization.marketAccount },
        }))
      );
    },
    [customerUserEndpoint]
  );

  const addMarketAccountAuthorization = useCallback(
    (customerUserID: string, authorizations: MarketAccountAuthorization[]) => {
      return addOrDeleteMarketAccountAuthorization(customerUserID, authorizations, 'add');
    },
    [addOrDeleteMarketAccountAuthorization]
  );

  const deleteMarketAccountAuthorization = useCallback(
    (customerUserID: string, authorizations: MarketAccountAuthorization[]) => {
      return addOrDeleteMarketAccountAuthorization(customerUserID, authorizations, 'delete');
    },
    [addOrDeleteMarketAccountAuthorization]
  );

  return (
    <CustomersContext.Provider
      value={{
        customers,
        customerUsers,
        customerRoles,
        createCustomer,
        updateCustomer,
        deleteCustomer,
        createCustomerUser,
        createCustomerTrade,
        updateCustomerTrade,
        updateCustomerUser,
        updateCustomerUserRole,
        getOrderExecutionRulesGlobalStatus,
        updateOrderExecutionRulesGlobalStatus,
        getCustomerRoles,
        getCustomerConfigs,
        getCustomerUserAPIKeys,
        getCustomerUserConfigs,
        getEffectiveLinkForCustomerTransaction,
        removeCustomerUserConfig,
        upsertCustomerUserConfigs,
        upsertCustomerUserConfig,
        createCustomerUserAPIKey,
        deleteCustomerUserAPIKey,
        updateCustomerUserAPIKey,
        updateCustomerUserAPIKeyPermissions,
        createCustomerTradingLimit,
        updateCustomerTradingLimit,
        deleteCustomerTradingLimit,
        getCustomerExecutionRules,
        createCustomerExecutionRule,
        editCustomerExecutionRule,
        deleteCustomerExecutionRule,
        getCustomerFIXConnections,
        upsertCustomerFIXConnection,
        deleteCustomerFIXConnection,
        modifyCustomerOrderSpread,
        customerAddressesByCustomerAddressID,
        approvePendingApprovalTransaction,
        approvePendingTransferTransaction,
        initiatePendingTransferTransaction,
        rejectPendingApprovalTransaction,
        rejectPendingTransferTransaction,
        getCustomerWithdrawAddress,
        createCustomerAddress,
        confirmCustomerAddress,
        addMarketAccountAuthorization,
        deleteMarketAccountAuthorization,
        customersLoaded: !isLoading,
      }}
    >
      {props.children}
    </CustomersContext.Provider>
  );
});
